<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { PortalRole } from '@ucc/shared';
  import { APP_NAME, APP_SHORT } from '@ucc/shared';
  import { navForRole } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Badge from './Badge.svelte';
  import Button from './Button.svelte';
  import Icon from './Icon.svelte';

  let {
    route,
    children,
    forcePasswordChange = false,
  }: {
    route: string;
    children: Snippet;
    /** When true, only Account nav is available until password is changed. */
    forcePasswordChange?: boolean;
  } = $props();

  const items = $derived(navForRole(session.role));
  const roleTone = $derived(session.role as PortalRole);

  function toggleTheme() {
    session.setTheme(session.theme === 'dark' ? 'light' : 'dark');
  }

  async function signOut() {
    await session.logout();
  }

  let exitingSupport = $state(false);
  async function exitSupport() {
    exitingSupport = true;
    try {
      await session.exitSupport();
    } finally {
      exitingSupport = false;
    }
  }

  function routeLabel(routeName: string): string {
    const map: Record<string, string> = {
      dashboard: 'Dashboard',
      devices: 'Devices',
      groups: 'Groups',
      clients: 'Clients',
      staff: 'Staff users',
      ota: 'OTA packages',
      audit: 'Audit log',
      settings: 'Settings',
    };
    return map[routeName] ?? 'UCC';
  }

  function initials(name: string): string {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  }
</script>

<div
  class="shell"
  class:collapsed={session.sidebarCollapsed}
  data-role={session.role}
>
  <aside class="sidebar" aria-label="Primary">
    <div class="brand">
      <div class="mark" aria-hidden="true">
        <img class="mark-logo" src="/logo.svg" width="28" height="28" alt="" />
      </div>
      {#if !session.sidebarCollapsed}
        <div class="brand-text">
          <strong>{APP_SHORT}</strong>
          <span>Unarvu</span>
        </div>
      {/if}
    </div>

    <nav class="nav" class:nav-locked={forcePasswordChange}>
      {#if forcePasswordChange}
        <a class="nav-item active" href="#/account" title="Account">
          <Icon name="user" size={18} />
          {#if !session.sidebarCollapsed}
            <span>Account</span>
          {/if}
        </a>
        {#if !session.sidebarCollapsed}
          <p class="nav-lock-hint">Set a new password to unlock navigation.</p>
        {/if}
      {:else}
        {#each items as item (item.id)}
          <a
            class="nav-item"
            class:active={route === item.id}
            href={item.href}
            title={item.label}
          >
            <Icon name={item.icon} size={18} />
            {#if !session.sidebarCollapsed}
              <span>{item.label}</span>
            {/if}
          </a>
        {/each}
      {/if}
    </nav>
  </aside>

  <div class="main">
    <div class="sticky-top">
      {#if session.isSupportActing}
        <div class="support-banner" role="alert">
          <span>
            <strong>Support access active</strong> — acting as <strong>{session.user?.tenantName}</strong>
            on behalf of {session.user?.realTenantName}. Every action here is visible to and reversible
            by the client.
          </span>
          <Button variant="secondary" size="sm" disabled={exitingSupport} onclick={() => void exitSupport()}>
            {exitingSupport ? 'Exiting…' : 'Exit support access'}
          </Button>
        </div>
      {/if}
      <header class="topbar">
      <div class="top-left">
        <Button variant="ghost" size="sm" onclick={() => session.toggleSidebar()} aria-label="Toggle sidebar">
          <Icon name="menu" size={18} />
        </Button>
        <div class="title-block">
          <p class="eyebrow">{APP_NAME}</p>
          <h1>{routeLabel(route)}</h1>
        </div>
      </div>

      <div class="top-right">
        <Badge tone={roleTone}>{session.role}</Badge>
        <Button
          variant="secondary"
          size="sm"
          onclick={toggleTheme}
          aria-label="Toggle theme"
        >
          <Icon name={session.theme === 'dark' ? 'sun' : 'moon'} size={16} />
          {session.theme === 'dark' ? 'Light' : 'Dark'}
        </Button>
        <div class="user-chip" title={session.email}>
          <span class="avatar" aria-hidden="true">{initials(session.displayName)}</span>
          <div class="user-meta">
            <strong>{session.displayName}</strong>
            <span>{session.email}</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onclick={signOut} aria-label="Sign out">
          Sign out
        </Button>
      </div>
      </header>
    </div>

    <main class="content">
      <div class="content-inner">
        {#if session.welcomeBanner}
          <div class="welcome-banner" role="status">
            <div class="welcome-banner-text">
              <strong>Password updated</strong>
              <p>{session.welcomeBanner}</p>
            </div>
            <button
              type="button"
              class="welcome-dismiss"
              onclick={() => session.dismissWelcomeBanner()}
            >
              Dismiss
            </button>
          </div>
        {/if}
        {@render children()}
      </div>
    </main>
  </div>
</div>

<style>
  .shell {
    --role-color: var(--role-admin);
    display: grid;
    grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
    min-height: 100vh;
    transition: grid-template-columns var(--duration-slow) var(--ease-out);
  }

  .shell[data-role='user'] {
    --role-color: var(--role-user);
  }

  .shell[data-role='client'] {
    --role-color: var(--role-client);
  }

  .shell.collapsed {
    grid-template-columns: var(--sidebar-collapsed) minmax(0, 1fr);
  }

  .sidebar {
    position: sticky;
    top: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    padding: var(--space-5) var(--space-3);
    border-right: 1px solid var(--border);
    background: color-mix(in srgb, var(--bg-sunken) 75%, transparent);
    backdrop-filter: blur(16px);
  }

  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: 0 var(--space-2);
    min-height: 2.5rem;
  }

  .mark {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: var(--radius-md);
    display: grid;
    place-items: center;
    color: var(--accent-fg);
    background: color-mix(in srgb, var(--bg-elevated) 88%, var(--brand-500));
    border: 1px solid color-mix(in srgb, var(--border) 70%, var(--brand-400));
    box-shadow: var(--shadow-sm);
    flex-shrink: 0;
    overflow: hidden;
    padding: 0.2rem;
  }

  .mark-logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  .brand-text {
    display: flex;
    flex-direction: column;
    line-height: 1.15;
    min-width: 0;
  }

  .brand-text strong {
    font-size: var(--text-md);
    letter-spacing: -0.02em;
  }

  .brand-text span {
    font-size: var(--text-xs);
    color: var(--text-muted);
  }

  .nav {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    flex: 1;
    overflow: auto;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: 2.5rem;
    padding: 0 var(--space-3);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    text-decoration: none;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    transition:
      background var(--duration) var(--ease-out),
      color var(--duration) var(--ease-out);
  }

  .nav-item:hover {
    background: var(--bg-hover);
    color: var(--text);
    text-decoration: none;
  }

  .nav-item.active {
    background: var(--bg-active);
    color: var(--text);
    box-shadow: inset 3px 0 0 var(--role-color);
  }

  .main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  .sticky-top {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .support-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4, 1rem);
    padding: 0.6rem var(--page-pad);
    background: #b45309;
    color: #fffbeb;
    font-size: var(--text-sm, 0.9rem);
    line-height: 1.4;
  }

  .support-banner strong {
    font-weight: 700;
  }

  .topbar {
    height: var(--topbar-height);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: 0 var(--page-pad);
    border-bottom: 1px solid var(--border);
    background: color-mix(in srgb, var(--bg) 82%, transparent);
    backdrop-filter: blur(14px);
  }

  .top-left,
  .top-right {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-width: 0;
  }

  .title-block {
    min-width: 0;
  }

  .eyebrow {
    font-size: var(--text-xs);
    color: var(--text-muted);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .title-block h1 {
    font-size: var(--text-lg);
    font-weight: var(--weight-semibold);
  }

  .user-chip {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-2) var(--space-1) var(--space-1);
    border: 1px solid var(--border);
    border-radius: var(--radius-pill);
    background: var(--surface);
    max-width: 16rem;
  }

  .avatar {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 0.65rem;
    font-weight: var(--weight-bold);
    color: var(--accent-fg);
    background: linear-gradient(145deg, var(--role-color), var(--brand-600));
  }

  .user-meta {
    display: none;
    flex-direction: column;
    min-width: 0;
    line-height: 1.15;
    padding-right: var(--space-1);
  }

  .user-meta strong {
    font-size: var(--text-sm);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-meta span {
    font-size: 0.7rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .content {
    flex: 1;
    padding: var(--page-pad);
  }

  .content-inner {
    max-width: var(--content-max);
    margin: 0 auto;
  }

  .welcome-banner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-4, 1rem);
    margin: 0 0 var(--space-5, 1.25rem);
    padding: 0.85rem 1rem;
    border-radius: 0.75rem;
    border: 1px solid color-mix(in srgb, var(--brand-500, #3b82f6) 35%, transparent);
    background: color-mix(in srgb, var(--brand-500, #3b82f6) 12%, var(--surface, #0f172a));
    color: var(--text, #e5e7eb);
  }

  .welcome-banner-text {
    min-width: 0;
  }

  .welcome-banner-text strong {
    display: block;
    font-size: var(--text-sm, 0.9rem);
    margin-bottom: 0.2rem;
  }

  .welcome-banner-text p {
    margin: 0;
    font-size: var(--text-sm, 0.9rem);
    color: var(--text-muted, #9ca3af);
    line-height: 1.4;
  }

  .welcome-dismiss {
    flex-shrink: 0;
    border: 0;
    background: transparent;
    color: var(--text-muted, #9ca3af);
    font-size: var(--text-sm, 0.85rem);
    cursor: pointer;
    padding: 0.25rem 0.4rem;
    border-radius: 0.35rem;
  }

  .welcome-dismiss:hover {
    color: var(--text, #e5e7eb);
    background: color-mix(in srgb, var(--text, #fff) 8%, transparent);
  }

  @media (min-width: 900px) {
    .user-meta {
      display: flex;
    }
  }

  @media (max-width: 800px) {
    .shell,
    .shell.collapsed {
      grid-template-columns: 1fr;
    }

    .sidebar {
      position: fixed;
      inset: 0 auto 0 0;
      width: min(18rem, 86vw);
      z-index: 30;
      transform: translateX(-105%);
      transition: transform var(--duration-slow) var(--ease-out);
    }

    .shell:not(.collapsed) .sidebar {
      transform: translateX(0);
      box-shadow: var(--shadow-lg);
    }

    .topbar {
      padding-inline: var(--space-4);
    }

    .content {
      padding: var(--space-4);
    }
  }
  .nav-lock-hint {
    margin: 0.5rem 0.75rem 0;
    font-size: var(--text-xs, 0.75rem);
    color: var(--warning, #b45309);
    line-height: 1.35;
  }

</style>
